import { useRef, useCallback } from 'react';

interface UseScratchCanvasOptions {
  onRevealed?: () => void;
  revealThreshold?: number; // percent 0-100, default 55
}

export function drawScratchSurface(canvas: HTMLCanvasElement, width: number, height: number) {
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.max(1, Math.floor(width * dpr));
  canvas.height = Math.max(1, Math.floor(height * dpr));
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = '#E4D9B8';
  ctx.fillRect(0, 0, width, height);
  ctx.font = '13px sans-serif';
  ctx.fillStyle = '#B8A469';
  for (let y = 14; y < height; y += 22) {
    for (let x = 0; x < width; x += 70) {
      ctx.fillText('PEPPAR', x, y);
    }
  }
}

export function useScratchCanvas(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  opts: UseScratchCanvasOptions = {}
) {
  const { onRevealed, revealThreshold = 55 } = opts;
  const isDrawing = useRef(false);
  const revealed = useRef(false);

  const scratchAt = useCallback(
    (clientX: number, clientY: number, onProgress: (pct: number) => void) => {
      const canvas = canvasRef.current;
      if (!canvas || revealed.current) return;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x = (clientX - rect.left) * scaleX;
      const y = (clientY - rect.top) * scaleY;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(x, y, 18 * scaleX, 0, Math.PI * 2);
      ctx.fill();
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      let cleared = 0;
      for (let i = 3; i < data.length; i += 4 * 20) {
        if (data[i] === 0) cleared++;
      }
      const pct = Math.min(100, Math.round((cleared / (data.length / (4 * 20))) * 100));
      onProgress(pct);
      if (pct > revealThreshold && !revealed.current) {
        revealed.current = true;
        onRevealed?.();
      }
    },
    [canvasRef, revealThreshold, onRevealed]
  );

  const handlers = useCallback(
    (onProgress: (pct: number) => void) => ({
      onMouseDown: (e: React.MouseEvent) => {
        isDrawing.current = true;
        scratchAt(e.clientX, e.clientY, onProgress);
      },
      onMouseUp: () => { isDrawing.current = false; },
      onMouseLeave: () => { isDrawing.current = false; },
      onMouseMove: (e: React.MouseEvent) => {
        if (isDrawing.current) scratchAt(e.clientX, e.clientY, onProgress);
      },
      onTouchStart: (e: React.TouchEvent) => {
        e.preventDefault();
        scratchAt(e.touches[0].clientX, e.touches[0].clientY, onProgress);
      },
      onTouchMove: (e: React.TouchEvent) => {
        e.preventDefault();
        scratchAt(e.touches[0].clientX, e.touches[0].clientY, onProgress);
      },
    }),
    [scratchAt]
  );

  return { handlers, isDrawing };
}
